<!-- eslint-disable vue/no-v-html -->
<template>
  <ThePopUp :is-open="isShowPopUp" :min-height="'auto'" @close="handleClose">
    <div class="py-2 tablet:px-2">
      <div class="relative mb-5">
        <strong class="block text-2xl">
          {{ contextTitle }}
        </strong>
      </div>
      <p
        class="mb-8 text-base text-gray-600"
        v-html="decode(contextDescription)"
      />
      <div class="mt-4 tablet:flex tablet:justify-end">
        <TheButton
          v-if="loginState.errorType === EnumLoginErrorType.E"
          class="mb-4 block w-full tablet:mb-0 tablet:mr-4 tablet:w-[120px]"
          :title="t('type-E_cancel')"
          type="outline"
          @click="handleClose"
        >
          <span class="text-sm">{{ t('type-E_cancel') }}</span>
        </TheButton>
        <TheButton
          v-if="loginState.errorType === EnumLoginErrorType.F"
          class="mb-4 block w-full tablet:mb-0 tablet:mr-4 tablet:w-[120px]"
          :title="t('type-F_login')"
          type="outline"
          @click="handleLogin"
        >
          <span class="text-sm">{{ t('type-F_login') }}</span>
        </TheButton>
        <TheButton
          class="block w-full tablet:w-[120px]"
          :title="contactUsText"
          :to="`/${locale}/marketplace/help/technical-support`"
          @click="handleClose"
        >
          <span class="text-sm">{{ contactUsText }}</span>
        </TheButton>
      </div>
    </div>
  </ThePopUp>
</template>

<script setup lang="ts">
  import { decode } from 'entities';

  import { EnumLoginErrorType } from '@/stores/mainStore';

  const emits = defineEmits<{ (e: 'close'): void }>();

  const runtimeConfig = useRuntimeConfig();
  const { ENVIRONMENT, API_DOMAIN_GLOBAL, API_DOMAIN_ACN } =
    runtimeConfig.public;

  const { t, locale } = useI18n({ useScope: 'local' });
  const mainStore = useMainStore();
  const { loginState } = storeToRefs(mainStore);
  const { isCN } = useLanguage();

  const errorType = computed(
    (): EnumLoginErrorType | null => loginState.value.errorType
  );

  const isShowPopUp = computed((): boolean => errorType.value !== null);

  const contextTitle = computed((): string => {
    switch (errorType.value) {
      case EnumLoginErrorType.E:
        return t('type-E_title');
      case EnumLoginErrorType.F:
        return t('type-F_title');
      default:
        return '';
    }
  });

  const contextDescription = computed((): string => {
    switch (errorType.value) {
      case EnumLoginErrorType.E:
        return t('type-E_description');
      case EnumLoginErrorType.F:
        return t('type-F_description');
      default:
        return '';
    }
  });

  const contactUsText = computed((): string => {
    switch (errorType.value) {
      case EnumLoginErrorType.E:
        return t('type-E_contact-us');
      case EnumLoginErrorType.F:
        return t('type-F_contact-us');
      default:
        return '';
    }
  });

  const handleClose = (): void => {
    loginState.value.errorType = null;
    emits('close');
  };

  const handleLogin = (): void => {
    const domain = isCN.value ? API_DOMAIN_GLOBAL : API_DOMAIN_ACN;
    const locale = isCN.value ? EnumLocale.EN_INT : EnumLocale.ZH_CN;
    const url: string = provideDomain(locale, ENVIRONMENT);
    window.location.href = `${domain}/marketplace/api/v1/sso/login?websiteCode=${locale}&callbackurl=${url}`;
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-jp": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-tw": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-kr": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-americas": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-asia-pacific": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "en-emea": {
    "type-E_title": "We couldn't sign you in — missing account info",
    "type-E_description": "Your account is missing region details, so we couldn't direct you properly.<br/><br/>Need help? Contact us — we're here for you.",
    "type-E_contact-us": "Contact us",
    "type-E_cancel": "Cancel",
    "type-F_title": "Let's get you to the right login page",
    "type-F_description": "It looks like your account is associated with WISE-IoT (China site).<br/>To continue, please go to sign in through the correct portal.<br/><br/>Need a hand? Our team is happy to help — just contact us.",
    "type-F_login": "Go to login",
    "type-F_contact-us": "Contact us"
  },
  "ja-jp": {
    "type-E_title": "サインインできませんでした — アカウント情報が不足しています",
    "type-E_description": "アカウントに地域情報が登録されていないため、正しいサイトにリダイレクトできませんでした。<br/><br/>サポートが必要な場合は、お気軽にお問い合わせください。",
    "type-E_contact-us": "お問い合わせ",
    "type-E_cancel": "キャンセル",
    "type-F_title": "正しいログインページにご案内します",
    "type-F_description": "お客様のアカウントは<strong>研華工業クラウド</strong>に関連付けられているようです。<br/>サービスを継続するには、正しいポータルからログインしてください。<br/><br/>ご不明な点がございましたら、お気軽にお問い合わせください。",
    "type-F_login": "ログインページへ",
    "type-F_contact-us": "キャンセル"
  },
  "zh-cn": {
    "type-E_title": "无法登录 — 账户信息不完整",
    "type-E_description": "您的账户缺少地区信息，因此无法正确跳转相关内容。<br/><br/>欢迎联系我们，我们乐意为您提供支持。",
    "type-E_contact-us": "联系我们",
    "type-E_cancel": "取消",
    "type-F_title": "我们将带您前往正确的登入页面",
    "type-F_description": "系统检测到您使用的账户属于 WISE 國際站，请前往 WISE 國際站登录。<br/>若您需要任何协助，欢迎随时联络我们，我们很乐意为您服务。",
    "type-F_login": "前往登录",
    "type-F_contact-us": "联系我们"
  },
  "zh-tw": {
    "type-E_title": "無法登入 — 帳戶資訊不完整",
    "type-E_description": "您的帳戶缺少地區資訊，因此無法正確導向相關內容。<br/><br/>歡迎與我們聯繫，我們樂意為您提供支援。",
    "type-E_contact-us": "聯繫我們",
    "type-E_cancel": "取消",
    "type-F_title": "我們將帶您前往正確的登入頁面",
    "type-F_description": "系統偵測到您的帳戶屬於<strong>研華工業雲</strong>，請透過正確的入口登入以繼續使用服務。<br/><br/>需要協助嗎？歡迎隨時聯絡我們，我們很樂意為您服務。",
    "type-F_login": "前往登入",
    "type-F_contact-us": "聯繫我們"
  }
}
</i18n>
